import { supabase } from '@/integrations/supabase/client';
import type { CustomerInsert, DealInsert, ActivityInsert, TaskInsert, LeadSource } from '@/types/crm';
import { inferLeadSource, getStoredUTMParams } from '@/hooks/useUTMParams';
import { logAudit } from '@/utils/auditLog';

/**
 * Find an existing customer by email or phone, or create a new one.
 * Returns the customer ID.
 */
export async function findOrCreateCustomer(data: {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  creditRating?: string;
  dateOfBirth?: string;
}): Promise<string | null> {
  const utm = getStoredUTMParams();
  const leadSource = inferLeadSource(utm) as LeadSource;

  const { data: customerId, error } = await supabase.rpc('find_or_create_customer', {
    p_first_name: data.firstName,
    p_last_name: data.lastName,
    p_email: data.email || null,
    p_phone: data.phone || null,
    p_address: data.address || null,
    p_city: data.city || null,
    p_province: data.province || null,
    p_credit_rating: data.creditRating || null,
    p_date_of_birth: data.dateOfBirth || null,
    p_lead_source: leadSource,
    p_utm_source: utm.utm_source || null,
    p_utm_medium: utm.utm_medium || null,
    p_utm_campaign: utm.utm_campaign || null,
    p_referrer_url: utm.referrer_url || null,
    p_landing_page: utm.landing_page || null,
  });

  if (error) {
    console.error('Failed to find or create customer:', error);
    return null;
  }

  return customerId;
}

/**
 * Create a deal in the pipeline from a pre-approval application
 */
export async function createDealFromPreApproval(
  customerId: string,
  formData: {
    vehicleType: string;
    budget: string;
    creditRating: string;
    tradeIn: string;
  }
): Promise<string | null> {
  const priority = formData.creditRating === 'excellent' || formData.creditRating === 'very-good' ? 'high' : 'medium';
  
  const { data: dealId, error } = await supabase.rpc('create_deal_from_lead', {
    p_customer_id: customerId,
    p_title: `Pre-Approval — ${formData.vehicleType || 'Vehicle'}`,
    p_stage: 'prospect',
    p_notes: `Budget: ${formData.budget}\nCredit: ${formData.creditRating}\nTrade-in: ${formData.tradeIn}`,
    p_priority: priority
  });

  if (error) {
    console.error('Failed to create deal from pre-approval:', error);
    return null;
  }

  // Audit log — deal created from pre-approval
  if (dealId) {
    await logAudit({
      entityType: 'deal',
      entityId: dealId,
      action: 'created',
      metadata: {
        title: `Pre-Approval — ${formData.vehicleType || 'Vehicle'}`,
        source: 'pre_approval',
        customer_id: customerId,
        budget: formData.budget,
        credit_rating: formData.creditRating,
      },
    });
  }

  return dealId;
}

/**
 * Create a deal from a vehicle inquiry.
 * SECURITY: Pricing (purchase_cost, front_gross) is resolved server-side by the RPC function.
 * Never query internal cost data from the client browser.
 */
export async function createDealFromInquiry(
  customerId: string,
  vehicleId: string,
  vehicleName: string
): Promise<string | null> {
  const { data: dealId, error } = await supabase.rpc('create_deal_from_lead', {
    p_customer_id: customerId,
    p_vehicle_id: vehicleId,
    p_title: `Vehicle Inquiry — ${vehicleName}`,
    p_stage: 'prospect',
    p_notes: `Inquiry for ${vehicleName} from website`,
    p_priority: 'high',
  });

  if (error) {
    console.error('Failed to create deal from inquiry');
    return null;
  }

  // Audit log — deal created from vehicle inquiry
  if (dealId) {
    await logAudit({
      entityType: 'deal',
      entityId: dealId,
      action: 'created',
      metadata: {
        title: `Vehicle Inquiry — ${vehicleName}`,
        source: 'vehicle_inquiry',
        customer_id: customerId,
        vehicle_id: vehicleId,
      },
    });
  }

  return dealId;
}

/**
 * Log an activity to the timeline
 */
export async function logActivity(data: ActivityInsert): Promise<void> {
  const { error } = await supabase
    .from('activities')
    .insert(data as any);

  if (error) {
    console.error('Failed to log activity:', error);
  }
}

/**
 * Create a task
 */
export async function createTask(data: TaskInsert): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .insert(data as any);

  if (error) {
    console.error('Failed to create task:', error);
  }
}
